import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { companyInfo } from '../../utils/dataInfo';
import { getPaymentMode } from '../../utils/helpers';

// Helper function to get status name
const getStatusName = (status) => {
    return status === 1 ? 'Active' : 'Inactive';
};

// Helper function to format currency
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount) + " "+"sh";
};

// Helper function to format date
const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
};

// Helper function to get customer name - FIXED
const getCustomerName = (customerId, customerData) => {
    if (!customerData || !Array.isArray(customerData)) return 'Unknown';
    const customer = customerData.find(c => parseInt(c.id) === parseInt(customerId));
    return customer ? customer.customer_name : 'Unknown';
};

// Helper function to get branch name - FIXED
const getBranchName = (branchId, branchData) => {
    if (!branchData || !Array.isArray(branchData)) return 'Unknown';
    const branch = branchData.find(b => parseInt(b.id) === parseInt(branchId));
    return branch ? `${branch.name}` : 'Unknown';
};


// Manual table drawing function for PDF
const drawDecorrotionTable = (doc, data, startY) => {
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    const rowHeight = 12;
    
    // Column widths for 11 columns
    const colWidths = [10, 45, 25, 35, 25, 15, 25, 25, 25, 25, 30]; // #, Number, Date, Customer, Cost, Qty, Total, Paid, Balance, Status, Branch
    
    // Check if we need a new page
    let currentY = startY;
    const neededHeight = (data.length + 1) * rowHeight;
    
    if (currentY + neededHeight > pageHeight - 20) {
        doc.addPage('landscape');
        currentY = margin;
        
        // Redraw header on new page
        doc.setFillColor(25, 118, 210);
        doc.setTextColor(255, 255, 255);
        doc.setFont(undefined, 'bold');
        
        // Draw header background
        doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
        
        const headers = ['#', 'Number', 'Date', 'Customer', 'Cost', 'Qty', 'Total', 'Paid', 'Balance', 'Status', 'Branch'];
        let xPos = margin;
        
        headers.forEach((header, index) => {
            doc.text(header, xPos + 2, currentY + 8);
            xPos += colWidths[index];
        });
        
        // Draw header underline
        doc.setDrawColor(255, 255, 255);
        doc.setLineWidth(0.5);
        doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
        
        currentY += rowHeight;
        doc.setFont(undefined, 'normal');
        doc.setTextColor(0, 0, 0);
    }
    
    // Draw table header if not on new page
    if (startY === currentY) {
        doc.setFillColor(25, 118, 210);
        doc.setTextColor(255, 255, 255);
        doc.setFont(undefined, 'bold');
        
        // Draw header background
        doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
        
        const headers = ['#', 'Number', 'Date', 'Customer', 'Cost', 'Qty', 'Total', 'Paid', 'Balance', 'Status', 'Branch'];
        let xPos = margin;
        
        headers.forEach((header, index) => {
            doc.text(header, xPos + 2, currentY + 8);
            xPos += colWidths[index];
        });
        
        // Draw header underline
        doc.setDrawColor(255, 255, 255);
        doc.setLineWidth(0.5);
        doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
        
        currentY += rowHeight;
        doc.setFont(undefined, 'normal');
        doc.setTextColor(0, 0, 0);
    }
    
    // Draw table rows
    data.forEach((row, rowIndex) => {
        // Check for page break
        if (currentY + rowHeight > pageHeight - 20) {
            doc.addPage('landscape');
            currentY = margin;
            
            // Redraw header on new page
            doc.setFillColor(25, 118, 210);
            doc.setTextColor(255, 255, 255);
            doc.setFont(undefined, 'bold');
            
            // Draw header background
            doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
            
            const headers = ['#', 'Number', 'Date', 'Customer', 'Cost', 'Qty', 'Total', 'Paid', 'Balance', 'Status', 'Branch'];
            let xPos = margin;
            
            headers.forEach((header, index) => {
                doc.text(header, xPos + 2, currentY + 8);
                xPos += colWidths[index];
            });
            
            // Draw header underline
            doc.setDrawColor(255, 255, 255);
            doc.setLineWidth(0.5);
            doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
            
            currentY += rowHeight;
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0, 0, 0);
        }
        
        // Alternate row colors
        if (rowIndex % 2 === 0) {
            doc.setFillColor(245, 245, 245);
        } else {
            doc.setFillColor(255, 255, 255);
        }
        
        // Draw row background
        doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
        
        // Draw row borders
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.2);
        doc.line(margin, currentY, pageWidth - margin, currentY);
        
        // Draw row text
        let xPos = margin;
        row.forEach((cell, cellIndex) => {
            // Truncate text if too long
            let text = String(cell);
            if (cellIndex === 3 && text.length > 15) { // Customer name
                text = text.substring(0, 12) + '...';
            } else if (text.length > 20) {
                text = text.substring(0, 17) + '...';
            }
            
            doc.text(text, xPos + 2, currentY + 8);
            xPos += colWidths[cellIndex];
        });
        
        currentY += rowHeight;
    });
    
    return currentY;
};

// PDF export for Decorrotion - Shows only specified columns in landscape
export const exportToPDF = (decorrotions, branchData = [], placesData = [], customerData = [], filters = {}) => {
    console.log("customerData"+JSON.stringify(customerData))
    try {
        // Create PDF in landscape mode
        const doc = new jsPDF('landscape');
        
        // Set document properties
        doc.setProperties({
            title: 'Decorrotion Bookings Report',
            subject: 'Decorrotion Bookings Export',
            author: companyInfo.name,
            keywords: 'decorrotion, bookings, export, report',
            creator: companyInfo.name
        });
        
        // Title section
        doc.setFontSize(20);
        doc.setTextColor(25, 118, 210);
        doc.text('DECORROTION BOOKINGS REPORT', doc.internal.pageSize.width / 2, 20, { align: 'center' });
        
        // Company/System name with branch info - FIXED
        doc.setFontSize(12);
        doc.setTextColor(100, 100, 100);
        
        // Check if branch filter is applied and get branch name
        let branchTitle = companyInfo.name;
        if (filters.branch_id) {
            const branch = branchData.find(b => b.id === filters.branch_id);
            if (branch) {
                branchTitle = `${companyInfo.name} - ${branch.name} Branch`;
            }
        }
        doc.text(branchTitle, doc.internal.pageSize.width / 2, 30, { align: 'center' });
        
        // Generation details
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);
        let currentY = 40;
        
        // Report info in two columns
        const leftColumnX = 20;
        const rightColumnX = doc.internal.pageSize.width / 2 + 20;
        
        doc.text(`Report Generated: ${new Date().toLocaleString()}`, leftColumnX, currentY);
        doc.text(`Total Records: ${decorrotions.length}`, rightColumnX, currentY);
        currentY += 8;
        
        // Calculate totals
        if (decorrotions.length > 0) {
            const totalAmount = decorrotions.reduce((sum, decorrotion) => sum + parseFloat(decorrotion.total_amount || 0), 0);
            const totalPaid = decorrotions.reduce((sum, decorrotion) => sum + parseFloat(decorrotion.paid_amount || 0), 0);
            const totalBalance = decorrotions.reduce((sum, decorrotion) => sum + parseFloat(decorrotion.balance_amount || 0), 0);
            
            doc.text(`Total Amount: ${formatCurrency(totalAmount)}`, leftColumnX, currentY);
            doc.text(`Total Paid: ${formatCurrency(totalPaid)}`, rightColumnX, currentY);
            currentY += 8;
            doc.text(`Total Balance: ${formatCurrency(totalBalance)}`, leftColumnX, currentY);
            currentY += 8;
        }
        
        // Filters applied
        if (filters.customer_id || filters.place_name || filters.payment_method || filters.dateFrom || filters.dateTo || filters.status || filters.branch_id) {
            doc.setFontSize(9);
            doc.setTextColor(60, 60, 60);
            doc.text('Applied Filters:', leftColumnX, currentY);
            currentY += 6;
            
            let filterLines = [];
            
            // Customer filter - FIXED
            if (filters.customer_id) {
                const customer = customerData.find(c => String(c.id) === String(filters.customer_id));
                filterLines.push(`• Customer: ${customer ? customer.customer_name : 'Unknown'}`);
            }
            
            // Place filter - FIXED
            if (filters.place_name) {
                const place = placesData.find(p => String(p.id) === String(filters.place_name));
                filterLines.push(`• Place: ${place ? place.name : filters.place_name}`);
            }
            
            if (filters.payment_method) {
                filterLines.push(`• Payment: ${getPaymentMode(filters.payment_method)}`);
            }
            
            // Branch filter - FIXED
            if (filters.branch_id) {
                const branch = branchData.find(b => String(b.id) === String(filters.branch_id));
                filterLines.push(`• Branch: ${branch ? branch.name : 'Unknown'}`);
            }
            
            if (filters.status) filterLines.push(`• Status: ${getStatusName(filters.status)}`);
            if (filters.dateFrom) filterLines.push(`• From: ${new Date(filters.dateFrom).toLocaleDateString()}`);
            if (filters.dateTo) filterLines.push(`• To: ${new Date(filters.dateTo).toLocaleDateString()}`);
            
            // Display filters in two columns if many
            const midPoint = Math.ceil(filterLines.length / 2);
            const leftFilters = filterLines.slice(0, midPoint);
            const rightFilters = filterLines.slice(midPoint);
            
            const maxLines = Math.max(leftFilters.length, rightFilters.length);
            
            for (let i = 0; i < maxLines; i++) {
                if (leftFilters[i]) {
                    doc.text(leftFilters[i], leftColumnX + 5, currentY);
                }
                if (rightFilters[i]) {
                    doc.text(rightFilters[i], rightColumnX, currentY);
                }
                currentY += 5;
            }
            currentY += 5;
        }
        
        // Prepare table data with only specified columns
        const tableData = decorrotions.map((decorrotion, index) => [
            (index + 1).toString(), // #
            decorrotion.booking_no || '', // Number
            formatDate(decorrotion.created_at), // Date
            getCustomerName(decorrotion.customer_id, customerData), // Customer - FIXED
            formatCurrency(decorrotion.cost || 0), // Cost
            decorrotion.quantity.toString(), // Qty
            formatCurrency(decorrotion.total_amount || 0), // Total
            formatCurrency(decorrotion.paid_amount || 0), // Paid
            formatCurrency(decorrotion.balance_amount || 0), // Balance
            getStatusName(decorrotion.status), // Status
            getBranchName(decorrotion.branch_id, branchData) // Branch - FIXED
        ]);
        
        // Draw the table
        currentY = drawDecorrotionTable(doc, tableData, currentY + 10);
        
        // Add footer with page number
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            doc.text(
                `Page ${i} of ${pageCount}`,
                doc.internal.pageSize.width / 2,
                doc.internal.pageSize.height - 10,
                { align: 'center' }
            );
            doc.text(
                `Generated on: ${new Date().toLocaleDateString()}`,
                doc.internal.pageSize.width - 20,
                doc.internal.pageSize.height - 10,
                { align: 'right' }
            );
        }
        
        const fileName = `decorrotion_report_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Decorrotion PDF export failed:', error);
        throw new Error('Failed to export to PDF: ' + error.message);
    }
};

// Excel export for Decorrotion - Shows all attributes
export const exportToExcel = (decorrotions, branchData = [], placesData = [], customerData = [], filters = {}) => {
    try {
        // Prepare data for Excel with all attributes
        const excelData = decorrotions.map((decorrotion, index) => ({
            'No.': index + 1,
            'Booking ID': decorrotion.id,
            'Booking Number': decorrotion.booking_no || '',
            'Customer ID': decorrotion.customer_id,
            'Customer Name': getCustomerName(decorrotion.customer_id, customerData), // FIXED
            'Customer Phone': decorrotion.customer_phone || '',
            'Customer Email': decorrotion.customer_email || '',
            'Customer Address': decorrotion.customer_address || '',
            'Additional Info': decorrotion.additional_info || '',
            'Place Name': decorrotion.place_name || '',
            'Cost per Unit': parseFloat(decorrotion.cost || 0),
            'Quantity': parseFloat(decorrotion.quantity || 0),
            'Total Amount': parseFloat(decorrotion.total_amount || 0),
            'Paid Amount': parseFloat(decorrotion.paid_amount || 0),
            'Balance Amount': parseFloat(decorrotion.balance_amount || 0),
            'Payment Method ID': decorrotion.payment_method,
            'Payment Method': getPaymentMode(decorrotion.payment_method),
            'Status ID': decorrotion.status,
            'Status': getStatusName(decorrotion.status),
            'Branch ID': decorrotion.branch_id,
            'Branch': getBranchName(decorrotion.branch_id, branchData), // FIXED
            'Description': decorrotion.description || '',
            'Check-in Date': decorrotion.check_in_date || '',
            'Check-out Date': decorrotion.check_out_date || '',
            'Created Date': new Date(decorrotion.created_at).toLocaleDateString('en-GB'),
            'Created Time': new Date(decorrotion.created_at).toLocaleTimeString('en-GB'),
            'Updated Date': new Date(decorrotion.updated_at).toLocaleDateString('en-GB'),
            'Updated Time': new Date(decorrotion.updated_at).toLocaleTimeString('en-GB'),
            'Created At Full': new Date(decorrotion.created_at).toLocaleString('en-GB'),
            'Updated At Full': new Date(decorrotion.updated_at).toLocaleString('en-GB')
        }));

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(excelData);

        // Auto-size columns
        const maxWidth = excelData.reduce((widths, row) => {
            Object.keys(row).forEach((key, idx) => {
                const length = String(row[key]).length;
                if (!widths[idx] || length > widths[idx]) {
                    widths[idx] = length;
                }
            });
            return widths;
        }, []);
        
        worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Decorrotion Bookings');

        // Generate Excel file and download
        const fileName = `decorrotion_export_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Decorrotion Excel export failed:', error);
        throw new Error('Failed to export to Excel: ' + error.message);
    }
};

// CSV export for Decorrotion - Shows all attributes
export const exportToCSV = (decorrotions, branchData = [], placesData = [], customerData = []) => {
    try {
        const headers = [
            'Booking ID',
            'Booking Number',
            'Customer ID',
            'Customer Name',
            'Customer Phone',
            'Customer Email',
            'Customer Address',
            'Additional Info',
            'Place Name',
            'Cost per Unit',
            'Quantity',
            'Total Amount',
            'Paid Amount',
            'Balance Amount',
            'Payment Method ID',
            'Payment Method',
            'Status ID',
            'Status',
            'Branch ID',
            'Branch',
            'Description',
            'Check-in Date',
            'Check-out Date',
            'Created Date',
            'Created Time',
            'Updated Date',
            'Updated Time',
            'Created At Full',
            'Updated At Full'
        ];
        
        const csvData = decorrotions.map(decorrotion => [
            decorrotion.id,
            decorrotion.booking_no || '',
            decorrotion.customer_id,
            `"${getCustomerName(decorrotion.customer_id, customerData).replace(/"/g, '""')}"`, // FIXED
            `"${(decorrotion.customer_phone || '').replace(/"/g, '""')}"`,
            `"${(decorrotion.customer_email || '').replace(/"/g, '""')}"`,
            `"${(decorrotion.customer_address || '').replace(/"/g, '""')}"`,
            `"${(decorrotion.additional_info || '').replace(/"/g, '""')}"`,
            `"${(decorrotion.place_name || '').replace(/"/g, '""')}"`,
            parseFloat(decorrotion.cost || 0).toFixed(2),
            parseFloat(decorrotion.quantity || 0).toFixed(2),
            parseFloat(decorrotion.total_amount || 0).toFixed(2),
            parseFloat(decorrotion.paid_amount || 0).toFixed(2),
            parseFloat(decorrotion.balance_amount || 0).toFixed(2),
            decorrotion.payment_method,
            getPaymentMode(decorrotion.payment_method),
            decorrotion.status,
            getStatusName(decorrotion.status),
            decorrotion.branch_id,
            `"${getBranchName(decorrotion.branch_id, branchData).replace(/"/g, '""')}"`, // FIXED
            `"${(decorrotion.description || '').replace(/"/g, '""')}"`,
            `"${(decorrotion.check_in_date || '').replace(/"/g, '""')}"`,
            `"${(decorrotion.check_out_date || '').replace(/"/g, '""')}"`,
            `"${new Date(decorrotion.created_at).toLocaleDateString('en-GB')}"`,
            `"${new Date(decorrotion.created_at).toLocaleTimeString('en-GB')}"`,
            `"${new Date(decorrotion.updated_at).toLocaleDateString('en-GB')}"`,
            `"${new Date(decorrotion.updated_at).toLocaleTimeString('en-GB')}"`,
            `"${new Date(decorrotion.created_at).toLocaleString('en-GB')}"`,
            `"${new Date(decorrotion.updated_at).toLocaleString('en-GB')}"`
        ]);

        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `decorrotion_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        return { success: true };
    } catch (error) {
        console.error('Decorrotion CSV export failed:', error);
        throw new Error('Failed to export to CSV: ' + error.message);
    }
};